import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

import { Card, CardContent, Grid, Typography, IconButton, Switch } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { infoIconStyle, getInfoLink } from '../../Dashboards/Market/styles/style';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Backdrop from '@material-ui/core/Backdrop';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { Alert } from '@material-ui/lab';

import {
  COLOR_ACCENT_1_GRADIENT,
  COLOR_F_GRADIENT,
  COLOR_G_GRADIENT,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY,
  COLOR_PRIMARY_LIGHT,
  COLOR_TOOLTIP_BACKGROUND,
  exchangeRates,
} from 'src/const';
import { AverageStayLengths, BookingLeads, BookingRates } from 'src/interfaces/idashboard';
import { VerticalBardCardPropTypes } from 'src/interfaces/iproptypes';
import { convertCurrency } from 'src/utils/functions';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    maxHeight: 335,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      maxHeight: 330,
    },
  },
  iconButton: {
    padding: 5,
  },
  verticalBarHeaderText: {
    fontSize: 17,
    fontWeight: 400,
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 900,
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 900,
  },
  subtleText: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  rateText: {
    fontWeight: 900,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
}));

let colors: string[] = ['#ffab00', '#ffc400', '#ffd740', '#ffe57f'];

const parseKeyName = (key: string, title: string) => {
  if (title === 'Available Daily Rate' || title === 'Occupancy Rate') {
    return 'Next ' + key + ' Days';
  } else {
    return key + 'th';
  }
};

const parseData = (
  data: BookingLeads | AverageStayLengths | BookingRates,
  title: string,
  currency: string | null
) => {
  let revenueGraph: { key: string; revenue: number; color: string }[] = [];
  let bookingLeadsGraph: { key: string; bookingLead: number; color: string }[] = [];
  let averageStayLengthsGraph: { key: string; averageStayLength: number; color: string }[] = [];
  let availableDailyRatesGraph: { key: string; availableDailyRate: number; color: string }[] = [];
  let occupancyRatesGraph: { key: string; occupancyRate: number; color: string }[] = [];
  let counter: number = 0;
  if (title.includes('Revenue')) {
    for (let key in data) {
      revenueGraph.push({
        key: key,
        revenue: currency ? convertCurrency(currency, data[key]) : data[key],
        color: colors[counter],
      });
      counter = counter + 1;
    }
    return revenueGraph;
  } else if (title === 'Available Daily Rate') {
    for (let key in data) {
      availableDailyRatesGraph.push({
        key: parseKeyName(key, title),
        availableDailyRate: currency ? convertCurrency(currency, data[key]) : data[key],
        color: COLOR_ACCENT_1_GRADIENT[counter],
      });
      counter = counter + 1;
    }
    return availableDailyRatesGraph;
  } else if (title === 'Occupancy Rate') {
    for (let key in data) {
      occupancyRatesGraph.push({
        key: parseKeyName(key, title),
        occupancyRate: data[key],
        color: COLOR_G_GRADIENT[counter],
      });
      counter = counter + 1;
    }
    return occupancyRatesGraph;
  } else if (title === 'Booking Lead Time') {
    for (let key in data) {
      bookingLeadsGraph.push({
        key: parseKeyName(key, title),
        bookingLead: data[key],
        color: COLOR_F_GRADIENT[counter],
      });
      counter = counter + 1;
    }
    return bookingLeadsGraph;
  } else {
    for (let key in data) {
      averageStayLengthsGraph.push({
        key: parseKeyName(key, title),
        averageStayLength: data[key],
        color: [COLOR_PRIMARY_LIGHT, COLOR_PRIMARY, COLOR_PRIMARY_DARK][counter],
      });
      counter = counter + 1;
    }
    return averageStayLengthsGraph;
  }
};

const getUnit = (title: string, currency: string | null): string => {
  if (title.includes('Revenue') || title === 'Available Daily Rate') {
    return currency ? exchangeRates[currency].symbol : '$';
  } else if (title === 'Occupancy Rate') {
    return '%';
  } else {
    return '';
  }
};

const getDataKey = (title: string) => {
  if (title.includes('Revenue')) {
    return 'revenue';
  } else if (title === 'Available Daily Rate') {
    return 'availableDailyRate';
  } else if (title === 'Occupancy Rate') {
    return 'occupancyRate';
  } else if (title === 'Booking Lead Time') {
    return 'bookingLead';
  } else if (title === 'Average Length of Stay') {
    return 'averageStayLength';
  } else return;
};

const VerticalChartCard = ({
  title,
  data,
  isDisabled,
  state,
  handleChange,
  search,
  totalSupply,
  height,
  setSubscriptionModalOpen,
  currency,
}: VerticalBardCardPropTypes) => {
  const classes = useStyles();

  let parsedData = parseData(data, title, currency as string | null);

  /*
  const graphData = useMemo(() => {
    return dataPricing.map(value => ({ uv: value }));
  }, [dataPricing]);
*/

  let blurGraph = {};
  if (isDisabled) {
    blurGraph = {
      webkitFilter: 'blur(2px)',
    };
  }

  const verticalBarCardStyle = { height: height };
  const responsiveContainerHeight = 0.7 * height;

  const CustomTooltip = ({ active, payload, label, classes }) => {
    if (active && payload) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {payload[0].payload.key}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {title}: {payload[0].value}
              {payload[0].unit}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Card className={classes.root} style={verticalBarCardStyle}>
      <CardContent>
        <Grid container justify="space-between" alignItems="flex-start" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="h2" className={classes.verticalBarHeaderText}>
              {title}
              {!title.includes('Length') && (
                <IconButton
                  className={classes.iconButton}
                  onClick={() => window.open(getInfoLink())}
                >
                  <InfoOutlinedIcon style={infoIconStyle} />
                </IconButton>
              )}
            </Typography>
          </Grid>

          <Grid container item xs={12} style={{ position: 'relative', top: '0', left: '0' }}>
            {Object.keys(data).length === 0 && (
              <Alert severity="error" style={{ marginBottom: 10 }}>
                Not enough historical data for this market
              </Alert>
            )}
            <ResponsiveContainer height={responsiveContainerHeight} width="100%">
              <BarChart
                width="100%"
                height="100%"
                data={parsedData}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                }}
                style={blurGraph}
              >
                <XAxis
                  dataKey="key"
                  style={{
                    fontSize: '0.75rem',
                    fontFamily: 'Roboto',
                  }}
                />
                <YAxis
                  style={{
                    fontSize: '0.75rem',
                    fontFamily: 'Roboto',
                  }}
                />
                <Tooltip content={<CustomTooltip classes={classes} active payload label />} />
                <Bar
                  dataKey={getDataKey(title)}
                  isAnimationActive={true}
                  animationDuration={150}
                  unit={getUnit(title, currency as string | null)}
                >
                  {parsedData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {isDisabled && setSubscriptionModalOpen && (
              <Backdrop className={classes.backdrop} open={true} invisible={true}>
                <IconButton
                  onClick={() => {
                    setSubscriptionModalOpen(true);
                  }}
                >
                  <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
                </IconButton>
              </Backdrop>
            )}
          </Grid>

          {title.includes('Revenue') && state && (
            <Grid item container xs={12} justify="flex-end" alignItems="center">
              <Typography className={classes.subtleText}> {'see past data'} </Typography>

              <Switch
                checked={state.showHistoricalRev}
                onChange={handleChange}
                name="showHistoricalRev"
                inputProps={{ 'aria-label': 'secondary checkbox' }}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default VerticalChartCard;
